import { Checkbox, Col, DatePicker, Divider, Form, Input, Radio, Row } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { FC, memo, useEffect } from 'react'
import DescriptionOfCalculationPosition from './description-of-calculation-position'
import User from '../../../../../components/form-items/user/user'
import { PackageCopies, Pallets } from '../../../../../types/api/shipment'
import Lacquers from './form-items/lacquers/lacquers'
import Positions from './form-items/positions/positions'
import QuantityPerRoll from './form-items/quantity-per-roll/quantity-per-roll'
import MaterialsCard from './materials-card'
import { requiredDatePickerRules, requiredOneElementRules } from '../../../../../constants/forms/validation/rules'
import { outputDateFormat } from '../../../../../constants/constants'
import ClearableRadioGroup from '../../../../../components/custom-form-controls/clearable-radio-group/clearable-radio-group'
import UploadFile from '../../../../../components/form-items/upload-file/upload-file'
import FormValues from '../types/form-values'
import { windingDisplay, Windings } from '../../../../../types/api/shipment'


interface TabsPanelProps {
    name: number
}

const TabsPanel: FC<TabsPanelProps> = ({ name }) => {
    const form = Form.useFormInstance<FormValues>()
    const calcPos = Form.useWatch(['list', name, 'calculationPosition'], form) as any
    const hasFlipJob =
        (calcPos?.additional_jobs || []).some((job: any) => {
            const jobName = String(job?.name || '').toLowerCase()
            return jobName === 'переворот' || jobName.includes('переворот')
        }) || false

    const designerName = [name, 'designer']
    const previousShipment = calcPos?.shipment_position?.shipment
    const hasPreviousBlankPreview = Boolean(previousShipment?.blankPreviewFiles?.length)
    const reusePreviousBlankPreview = Form.useWatch(
        ['list', name, 'reusePreviousBlankPreview'],
        form
    )
    const positions = Form.useWatch(['list', name, 'positions'], form)
    const hasFaceTurnSplit = Boolean(
        positions?.some((pos: FormValues['list'][number]['positions'][number]) => pos?.side === 'face')
    )
    const isSourceUploadDisabled = Boolean(reusePreviousBlankPreview)

    useEffect(() => {
        if (hasFaceTurnSplit) {
            return
        }
        const current = form.getFieldValue(['list', name, 'windingSchemeTurn'])
        if (current != null) {
            form.setFieldValue(['list', name, 'windingSchemeTurn'], undefined)
        }
    }, [form, hasFaceTurnSplit, name])

    // const windingSchemes = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i']

    // const windingSchemeOptions = windingSchemes.map((item, i) => {
    //     return {
    //         value: item,
    //         label: (
    //             <>
    //                 <img src={`/images/windings/${item}.png`} alt={`Схема намотки ${item}`} />
    //             </>
    //         ),
    //     }
    // })
    const windingSchemes = Object.values(Windings)

    const windingSchemeOptions = windingSchemes.map(item => {
        return {
            value: item,
            label: (
                <>
                    <img src={`/images/windings/${item}.png`} alt={`Схема намотки ${windingDisplay[item]}`} />
                    <span style={{ marginLeft: 6 }}>{windingDisplay[item]}</span>
                </>
            ),
        }
    })

    return (
        <>
            <Form.Item name={'calculationPosition'} hidden>
                <Input />
            </Form.Item>

            {hasPreviousBlankPreview ? (
                <Form.Item
                    name={[name, 'reusePreviousBlankPreview']}
                    valuePropName='checked'
                    extra='Снимите галочку, если макет этикетки изменился и дизайнеру нужно отрисовать новый бланк.'
                >
                    <Checkbox>
                        Макет не изменился, использовать бланк из отгрузки №
                        {previousShipment?.id}
                    </Checkbox>
                </Form.Item>
            ) : null}

            <UploadFile
                name={[name, 'sourceFile']}
                label="Общий файл для отгрузки (исходники)"
                extra={
                    isSourceUploadDisabled
                        ? 'Исходники не нужны: используется бланк из прошлой отгрузки'
                        : 'Можно загрузить несколько файлов'
                }
                disabled={isSourceUploadDisabled}
                rules={[
                    {
                        validator() {
                            if (isSourceUploadDisabled) return Promise.resolve()
                                const value = form.getFieldValue(['list', name, 'sourceFile'])
                            if (value?.length) return Promise.resolve()
                            const positions = form.getFieldsValue().list[name].positions
                            const positionHaveFile = positions.every(
                                (pos: { sourceFile?: unknown[] }) =>
                                    pos?.sourceFile && pos.sourceFile.length > 0
                            )
                            if (!positionHaveFile) {
                                return Promise.reject(
                                    'Загрузите общий файл отгрузки или файлы для каждой позиции'
                                )
                            }
                            return Promise.resolve()
                        },
                    },
                ]}
            />
            <Positions namePath={name} sourceUploadDisabled={isSourceUploadDisabled}/>

            <Form.Item
                name={[name, 'clientDeliveryDate']}
                label={'Дата поставки клиенту'}
                rules={requiredDatePickerRules}
            >
                <DatePicker format={outputDateFormat} />
            </Form.Item>

            <DescriptionOfCalculationPosition name={name} />
            <br />
            <MaterialsCard namePath={name} />
            <Divider />

            <Row gutter={24}>
                <Col sm={6}>
                    <User
                        name={designerName}
                        label={'Дизайнер'}
                        positions={['Дизайнер']}
                        showActiveTaskCount
                    />
                </Col>
                <Col sm={24}>
                    <Form.Item
                        name={[name, 'windingSchemeFace']}
                        label={'Схема намотки (лицо)'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group style={{ display: 'flex', justifyContent: 'space-between' }}>
                            {windingSchemeOptions.map(item => (
                                <Radio value={item.value} key={item.value}>
                                    {item.label}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={24}>
                    <Form.Item
                        name={[name, 'windingSchemeTurn']}
                        label={'Схема намотки (оборот)'}
                        rules={hasFaceTurnSplit ? requiredOneElementRules : []}
                    >
                        <ClearableRadioGroup
                            options={windingSchemeOptions}
                            disabled={!hasFaceTurnSplit}
                        />
                    </Form.Item>
                </Col>
                <Col sm={6}>
                    <QuantityPerRoll name={name} />
                </Col>
                <Col sm={8}>
                    <Form.Item
                        name={[name, 'windingInfo']}
                        label={'Инфо по намотке'}
                        extra={'Будет выведено в бланках'}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col sm={6}>
                    <Form.Item
                        name={[name, 'bushing']}
                        label={'Втулка'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={40}>40 мм</Radio>
                            <Radio value={45}>45 мм</Radio>
                            <Radio value={76}>76 мм</Radio>
                            <Radio value={152}>152 мм</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={6}>
                    <Form.Item
                        name={[name, 'printingOnGlueLayer']}
                        label={'Печать на клеевом слое'}
                        rules={hasFlipJob ? requiredOneElementRules : []}
                    >
                        <Radio.Group disabled={!hasFlipJob}>
                            <Radio value={true}>Да</Radio>
                            <Radio value={false}>Нет</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
            </Row>

            <Lacquers namePath={name} />

            <Row gutter={24}>
                <Col sm={24}>
                    <Form.Item
                        name={[name, 'package']}
                        label={'Упаковка'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={'Стретч'}>Стретч</Radio>
                            <Radio value={'Стретч+гофро'}>Стретч+гофро</Radio>
                            <Radio value={'Стретч+гофро+паллет'}>Стретч+гофро+паллет</Radio>
                            <Radio value={'Стретч+паллет'}>Стретч+паллет</Radio>
                            <Radio value={'Короб'}>Короб</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={24}>
                    <Form.Item
                        name={[name, 'packageCopies']}
                        label={'Тираж упаковки'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={PackageCopies.Strictly}>
                                Строго по заявленному количеству
                            </Radio>
                            <Radio value={PackageCopies.NotStrictly}>Клиент возьмет больше</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={12}>
                    <Form.Item
                        name={[name, 'manualLabeling']}
                        label={'Этикеровка'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={false}>Автоматическая</Radio>
                            <Radio value={true}>Ручная</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={12}>
                    <Form.Item
                        name={[name, 'clientLabel']}
                        label={'Ярлык'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={false}>Гамма (наш)</Radio>
                            <Radio value={true}>Клиента</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={12}>
                    <Form.Item
                        name={[name, 'pallet']}
                        label={'Паллет'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={Pallets.Euro}>Евро</Radio>
                            <Radio value={Pallets.Any}>Любой</Radio>
                            <Radio value={Pallets.No}>Нет</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={12}>
                    <Form.Item
                        name={[name, 'congreve']}
                        label={'Конгрев'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={true}>Да</Radio>
                            <Radio value={false}>Нет</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={12}>
                    <Form.Item
                        name={[name, 'priming']}
                        label={'Праймировать материал'}
                        rules={requiredOneElementRules}
                    >
                        <Radio.Group>
                            <Radio value={true}>Да</Radio>
                            <Radio value={false}>Нет</Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
                <Col sm={24}>
                    <Form.Item name={[name, 'comment']} label={'Комментарий к заданию'}>
                        <TextArea />
                    </Form.Item>
                </Col>
            </Row>
            {/* <Button icon={<ArrowRightOutlined />}>Следующий вид</Button> */}
        </>
    )
}

export default memo(TabsPanel)
